import { Monitor, Smartphone, Globe } from 'lucide-react';

/* 01 — Ventes (ticket sombre) */
export function SalesDemo() {
  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-charcoal-800 p-6 text-white shadow-neobrutal-10">
      <div className="flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-charcoal-200">Ticket #T-2049</span>
        <span className="inline-flex items-center gap-1.5 text-lime-400">
          <span className="h-1.5 w-1.5 rounded-full bg-lime-400 animate-[pulse_2s_ease-in-out_infinite]" />
          En cours
        </span>
      </div>
      <div className="mt-5 font-marketing-display text-[56px] font-bold leading-none tracking-[-0.03em] text-lime-400 md:text-[72px]">
        8 200 <span className="text-2xl text-white">F</span>
      </div>
      <ul className="mt-6 space-y-2.5 font-marketing-body text-[14px]">
        {[
          ['2 × Riz 5kg Basmati', '6 000'],
          ['1 × Huile Dinor 1L', '1 600'],
          ['3 × Sachet lait', '600'],
        ].map(([q, p]) => (
          <li
            key={q}
            className="flex justify-between border-b border-dashed border-charcoal-600 pb-2"
          >
            <span>{q}</span>
            <span className="font-marketing-mono font-medium">{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex items-center justify-between border-t-2 border-charcoal-600 pt-4 font-marketing-display text-[18px] font-bold">
        <span>Total TTC</span>
        <span>8 200 F</span>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2.5">
        <button className="border-2 border-charcoal-800 bg-lime-500 py-2.5 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-neobrutal-3">
          Mobile Money
        </button>
        <button className="border-2 border-lime-500 bg-charcoal-700 py-2.5 font-marketing-display text-[13px] font-bold text-lime-400">
          Espèces
        </button>
      </div>
    </div>
  );
}

/* 02 — Stock (alertes) */
export function StockDemo() {
  const rows: ReadonlyArray<{ name: string; qty: string; state: 'ok' | 'low' | 'out' }> = [
    { name: 'Coca-Cola 33cl', qty: '42 en stock', state: 'ok' },
    { name: 'Riz Basmati 5kg', qty: '4 restants · seuil 10', state: 'low' },
    { name: 'Huile Dinor 1L', qty: '0 · rupture', state: 'out' },
  ];
  const stateStyles: Record<string, string> = {
    ok: 'bg-lime-500 text-charcoal-800',
    low: 'bg-terracotta-500 text-white',
    out: 'bg-charcoal-800 text-white',
  };
  const stateLabel: Record<string, string> = { ok: 'OK', low: 'Bas', out: 'Rupture' };

  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-10">
      <div className="flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-charcoal-500">Alertes stock · 3</span>
        <span className="text-terracotta-500">2 actions</span>
      </div>
      <ul className="mt-5 space-y-3">
        {rows.map((r) => (
          <li
            key={r.name}
            className="flex items-center justify-between border-2 border-charcoal-800 bg-paper px-4 py-3"
          >
            <div>
              <div className="font-marketing-display text-[14.5px] font-semibold text-charcoal-800">
                {r.name}
              </div>
              <div className="font-marketing-mono text-[11px] text-charcoal-500">{r.qty}</div>
            </div>
            <span
              className={`border-2 border-charcoal-800 px-2.5 py-1 font-marketing-mono text-[10px] font-bold uppercase tracking-[0.12em] ${stateStyles[r.state]}`}
            >
              {stateLabel[r.state]}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-5 border-t-2 border-dashed border-charcoal-200 pt-4">
        <div className="flex items-center justify-between font-marketing-mono text-[11px] uppercase tracking-[0.12em]">
          <span className="text-charcoal-500">Dernier mouvement</span>
          <span className="text-lime-600">il y a 2 min</span>
        </div>
      </div>
    </div>
  );
}

/* 03 — Catalogue (grid produits) */
export function CatalogDemo() {
  const items = [
    { letter: 'B', name: 'Basmati 5kg', price: '3 000 F', bg: 'bg-lime-500' },
    { letter: 'H', name: 'Huile Dinor 1L', price: '1 600 F', bg: 'bg-terracotta-500' },
    { letter: 'C', name: 'Coca 33cl', price: '400 F', bg: 'bg-charcoal-800 text-lime-400' },
  ];
  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-10">
      <div className="flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-charcoal-500">3 produits · page 1 / 42</span>
        <span className="text-lime-600">+ nouveau</span>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        {items.map((it) => (
          <div key={it.name} className="border-2 border-charcoal-800 bg-paper">
            <div
              className={`flex h-20 items-center justify-center font-marketing-display text-3xl font-bold text-white ${it.bg}`}
            >
              {it.letter}
            </div>
            <div className="border-t-2 border-charcoal-800 p-2.5">
              <div className="font-marketing-display text-[12px] font-semibold leading-tight text-charcoal-800">
                {it.name}
              </div>
              <div className="mt-1 font-marketing-mono text-[11px] font-semibold text-charcoal-700">
                {it.price}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2 border-t-2 border-dashed border-charcoal-200 pt-4">
        <span className="border-2 border-charcoal-800 bg-lime-500 px-3 py-1 font-marketing-mono text-[10px] font-bold uppercase tracking-[0.12em] text-charcoal-800">
          Épicerie 38
        </span>
        <span className="border-2 border-charcoal-800 bg-white px-3 py-1 font-marketing-mono text-[10px] font-bold uppercase tracking-[0.12em] text-charcoal-700">
          Boissons 14
        </span>
        <span className="border-2 border-charcoal-800 bg-white px-3 py-1 font-marketing-mono text-[10px] font-bold uppercase tracking-[0.12em] text-charcoal-700">
          Hygiène 7
        </span>
      </div>
    </div>
  );
}

/* 04 — Multi-boutique */
export function MultiShopDemo() {
  const shops = [
    {
      dot: 'bg-lime-500',
      name: 'Velko Plateau',
      city: 'Dakar · SN',
      sales: '1 842 000 F',
      trend: '+22%',
      trendColor: 'text-lime-600',
    },
    {
      dot: 'bg-terracotta-500',
      name: 'Velko Treichville',
      city: 'Abidjan · CI',
      sales: '1 204 000 F',
      trend: '+9%',
      trendColor: 'text-lime-600',
    },
    {
      dot: 'bg-charcoal-700',
      name: 'Velko Bonanjo',
      city: 'Douala · CM',
      sales: '896 000 F',
      trend: '−3%',
      trendColor: 'text-terracotta-500',
    },
  ];
  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-10">
      <div className="flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-charcoal-500">3 boutiques · ce mois</span>
        <span className="text-lime-600">+14%</span>
      </div>
      <ul className="mt-5 space-y-2.5">
        {shops.map((s) => (
          <li
            key={s.name}
            className="grid grid-cols-[12px_1fr_auto_auto] items-center gap-3 border-2 border-charcoal-800 bg-paper px-4 py-3"
          >
            <span className={`h-3 w-3 rounded-full border-2 border-charcoal-800 ${s.dot}`} />
            <div>
              <div className="font-marketing-display text-[14px] font-semibold text-charcoal-800">
                {s.name}
              </div>
              <div className="font-marketing-mono text-[10px] uppercase tracking-[0.1em] text-charcoal-500">
                {s.city}
              </div>
            </div>
            <div className="font-marketing-display text-[14px] font-bold text-charcoal-800">
              {s.sales}
            </div>
            <div className={`font-marketing-mono text-[12px] font-bold ${s.trendColor}`}>
              {s.trend}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* 05 — Offline (queue) */
export function OfflineDemo() {
  const items = [
    { label: 'Vente #T-2048 · 4 200 F', ts: '14:02', synced: true },
    { label: 'Vente #T-2049 · 8 200 F', ts: '14:07', synced: false },
    { label: 'Réappro stock · 12 produits', ts: '14:15', synced: false },
  ];
  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-charcoal-800 p-6 text-white shadow-neobrutal-10">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 border-2 border-lime-500 bg-charcoal-700 px-3 py-1 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.12em] text-lime-400">
          <span className="h-2 w-2 rounded-full bg-lime-500 animate-[pulse_2s_ease-in-out_infinite]" />
          Hors ligne
        </span>
        <span className="font-marketing-mono text-[11px] uppercase tracking-[0.1em] text-charcoal-200">
          12 en attente
        </span>
      </div>
      <ul className="mt-5 space-y-2.5">
        {items.map((it) => (
          <li
            key={it.label}
            className="grid grid-cols-[14px_1fr_auto] items-center gap-3 border-2 border-charcoal-700 bg-charcoal-700 px-3 py-2.5"
          >
            <span
              className={`h-2.5 w-2.5 rounded-full ${it.synced ? 'bg-lime-500' : 'bg-terracotta-500 animate-[pulse_2s_ease-in-out_infinite]'}`}
            />
            <span className="font-marketing-body text-[13.5px]">{it.label}</span>
            <span className="font-marketing-mono text-[11px] text-charcoal-200">{it.ts}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 border-t-2 border-charcoal-700 pt-4 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-lime-400">
        → Auto-sync dès retour réseau
      </div>
    </div>
  );
}

/* 06 — Équipe (PIN pad) */
export function TeamDemo() {
  const members = [
    { name: 'Aïcha · Caissière', role: 'Ventes', pin: '●●●●', color: 'bg-lime-500' },
    { name: 'Kouassi · Gérant', role: 'Admin complet', pin: '●●●●', color: 'bg-terracotta-500' },
    {
      name: 'Samuel · Stock',
      role: 'Inventaire',
      pin: '●●●●',
      color: 'bg-charcoal-700 text-lime-400',
    },
  ];
  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-10">
      <div className="flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-charcoal-500">Équipe · Plateau</span>
        <span className="text-lime-600">+ ajouter</span>
      </div>
      <ul className="mt-5 space-y-3">
        {members.map((m) => (
          <li
            key={m.name}
            className="flex items-center gap-4 border-2 border-charcoal-800 bg-paper px-4 py-3"
          >
            <span
              className={`flex h-11 w-11 items-center justify-center border-2 border-charcoal-800 font-marketing-display text-[14px] font-bold text-charcoal-800 ${m.color}`}
            >
              {m.name.slice(0, 1)}
            </span>
            <div className="flex-1">
              <div className="font-marketing-display text-[14.5px] font-semibold text-charcoal-800">
                {m.name}
              </div>
              <div className="font-marketing-mono text-[10.5px] uppercase tracking-[0.1em] text-charcoal-500">
                {m.role}
              </div>
            </div>
            <span className="font-marketing-mono text-sm font-bold tracking-[0.2em] text-charcoal-400">
              {m.pin}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* 07 — Rapports (chart bars) */
export function ReportsDemo() {
  const bars = [
    { d: 'L', h: 40 },
    { d: 'M', h: 55 },
    { d: 'M', h: 48 },
    { d: 'J', h: 72 },
    { d: 'V', h: 92, hi: true },
    { d: 'S', h: 100, hi: true },
    { d: 'D', h: 60 },
  ];
  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-10">
      <div className="flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-charcoal-500">Cette semaine</span>
        <span className="text-lime-600">+18%</span>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2">
        {[
          { l: 'CA', v: '1.84M', d: '+18%' },
          { l: 'Tickets', v: '482', d: '+7%' },
          { l: 'Panier moy.', v: '3 820F', d: '+10%' },
        ].map((k) => (
          <div key={k.l} className="border-2 border-charcoal-800 bg-paper px-3 py-2.5">
            <div className="font-marketing-mono text-[10px] uppercase tracking-[0.14em] text-charcoal-500">
              {k.l}
            </div>
            <div className="mt-1 font-marketing-display text-lg font-bold text-charcoal-800">
              {k.v}
            </div>
            <div className="font-marketing-mono text-[10px] text-lime-600">{k.d}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 border-2 border-charcoal-800 bg-paper p-4">
        <div className="font-marketing-mono text-[10px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
          Ventes · Lun → Dim
        </div>
        <div className="mt-4 flex h-32 items-end gap-2">
          {bars.map((b, i) => (
            <div
              key={`${b.d}-${i}`}
              className={`flex-1 border-2 border-charcoal-800 transition-[height] ${b.hi ? 'bg-lime-500' : 'bg-white'}`}
              style={{ height: `${b.h}%` }}
              aria-label={`${b.d} : ${b.h}%`}
            />
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          {bars.map((b, i) => (
            <div
              key={`lbl-${b.d}-${i}`}
              className="flex-1 text-center font-marketing-mono text-[10px] font-medium text-charcoal-500"
            >
              {b.d}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* 08 — Multi-plateforme */
export function PlatformsDemo() {
  const platforms = [
    { Icon: Monitor, name: 'Desktop', sub: 'Caisse · comptoir', focus: false },
    { Icon: Smartphone, name: 'Mobile', sub: 'Livreur · terrain', focus: true },
    { Icon: Globe, name: 'Web', sub: 'Gérant · maison', focus: false },
  ];
  return (
    <div className="w-full max-w-lg border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-10">
      <div className="flex items-center justify-between font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-charcoal-500">Synchronisé temps réel</span>
        <span className="inline-flex items-center gap-1.5 text-lime-600">
          <span className="h-1.5 w-1.5 rounded-full bg-lime-500" />3 appareils
        </span>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        {platforms.map((p) => (
          <div
            key={p.name}
            className={`flex flex-col items-center gap-3 border-2 border-charcoal-800 p-4 text-center ${p.focus ? 'bg-lime-500 shadow-neobrutal-4' : 'bg-paper'}`}
          >
            <p.Icon className="h-7 w-7 text-charcoal-800" strokeWidth={2} />
            <div>
              <div className="font-marketing-display text-[14.5px] font-bold text-charcoal-800">
                {p.name}
              </div>
              <div className="mt-1 font-marketing-mono text-[10px] uppercase tracking-[0.1em] text-charcoal-700">
                {p.sub}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-between border-2 border-charcoal-800 bg-paper px-4 py-2.5">
        <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-600">
          Dernière synchro
        </span>
        <span className="font-marketing-display text-sm font-bold text-lime-600">il y a 12 s</span>
      </div>
    </div>
  );
}
